import { useState, type ComponentType } from 'react';

interface OAuthButtonProps {
  id: string;
  label: string;
  /** When set, the button becomes a clickable link to this URL (OAuth initiate). */
  href?: string;
  /** When href is unset, button shows disabled + tap-to-see tooltip. */
  disabledReason?: string;
  Icon: ComponentType<{ size?: number; className?: string }>;
  iconColor?: string;
}

export function OAuthButtons({ providers }: { providers: OAuthButtonProps[] }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      {providers.map((p) =>
        p.href ? (
          <OAuthLinkButton key={p.id} {...(p as OAuthLinkProps)} />
        ) : (
          <OAuthDisabledButton key={p.id} {...(p as OAuthDisabledProps)} />
        ),
      )}
    </div>
  );
}

type OAuthLinkProps = OAuthButtonProps & { href: string };
type OAuthDisabledProps = OAuthButtonProps & { disabledReason: string };

function OAuthLinkButton({ label, Icon, iconColor, href }: OAuthLinkProps) {
  return (
    <a
      href={href}
      className="group w-full flex items-center justify-center gap-2.5 py-2.5 rounded-lg border border-surface-border bg-surface-card text-brand-white hover:border-accent/30 hover:bg-surface-hover transition-all"
    >
      <Icon size={18} className={iconColor} />
      <span className="text-sm font-medium">{label}</span>
    </a>
  );
}

function OAuthDisabledButton({ label, disabledReason, Icon, iconColor }: OAuthDisabledProps) {
  const [note, setNote] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        aria-disabled="true"
        onClick={() => setNote((n) => !n)}
        className="group w-full flex items-center justify-center gap-2.5 py-2.5 rounded-lg border border-surface-border bg-surface-card text-brand-muted opacity-50 hover:opacity-70 transition-opacity"
      >
        <Icon size={18} className={iconColor ?? undefined} />
        <span className="text-sm font-medium">{label}</span>
      </button>
      <span
        className={`pointer-events-none absolute -bottom-7 left-1/2 -translate-x-1/2 text-[10px] text-brand-muted/80 whitespace-nowrap transition-opacity ${note ? 'opacity-100' : 'opacity-0'}`}
      >
        {disabledReason}
      </span>
    </div>
  );
}